import React, { useEffect, useState, useRef } from "react";
import {
  Plus, Download, FileText, Receipt, Coins, Pencil, Trash2,
  Paperclip, Mail, FolderArchive, Tags, X, Upload, CheckCircle2, Wallet, RefreshCw, Tag, CalendarDays,
} from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, SearchInput, Btn, Field, Input, Select, Textarea, Modal, ConfirmDialog } from "@/components/Primitives";
import { StatusBadge } from "@/components/StatusBadge";
import { CostImportModal, BulkAttachModal } from "@/components/CostImport";
import { api, fmtCurrency, formatApiErrorDetail, fmtDate, API, can, saveBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const INV_TYPES = [
  { v: "customer", l: "Customer" },
  { v: "supplier", l: "Supplier" },
  { v: "processing", l: "Processing" },
  { v: "storage", l: "Storage" },
  { v: "transport", l: "Transport" },
  { v: "dispatch", l: "Dispatch" },
];

const blankLine = () => ({ description: "", unit: "", quantity: 1, unit_price: 0, total: 0 });

const UNITS = ["Units", "Tons", "Litres", "Hours", "Days", "km", "Load", "kg", "m³", "Each"];

const emptyInvoice = () => ({
  invoice_number: "",
  invoice_type: "customer",
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  customer_id: "", customer_name: "",
  supplier_id: "", supplier_name: "",
  order_id: "", order_number: "",
  lines: [blankLine()],
  vat_rate: 15,
  currency: "ZAR",
  notes: "",
});

export default function Finance() {
  const { user } = useAuth();
  const [tab, setTab] = useState("invoices");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [payOpen, setPayOpen] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [creditOpen, setCreditOpen] = useState(null);
  const [creditAmt, setCreditAmt] = useState("");
  const [creditInfo, setCreditInfo] = useState({ available: 0 });

  const openApplyCredit = async (r) => {
    setCreditOpen(r);
    setCreditAmt("");
    setCreditInfo({ available: 0 });
    try {
      const c = await api.get(`/finance/customers/${r.customer_id}/credit`);
      const bal = r.total - (r.amount_paid || 0);
      const dflt = Math.min(c.data.available || 0, bal);
      setCreditInfo({ available: c.data.available || 0 });
      setCreditAmt(dflt > 0 ? Math.round(dflt * 100) / 100 : "");
    } catch { /* no credit */ }
  };
  const submitApplyCredit = async () => {
    try {
      await api.post(`/finance/invoices/${creditOpen.id}/apply-credit`, { amount: Number(creditAmt) });
      toast.success("Credit applied to invoice");
      setCreditOpen(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const [emailTarget, setEmailTarget] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [mail, setMail] = useState({ configured: false, email: null });
  const isAdmin = user?.role === "admin";

  const [form, setForm] = useState(emptyInvoice());
  const [creditAvail, setCreditAvail] = useState(0);
  const [applyCredit, setApplyCredit] = useState("");

  const loadCredit = async (cid) => {
    if (!cid) { setCreditAvail(0); setApplyCredit(""); return; }
    try {
      const r = await api.get(`/finance/customers/${cid}/credit`);
      setCreditAvail(r.data.available || 0);
    } catch { setCreditAvail(0); }
  };

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (type) params.invoice_type = type;
    const r = await api.get("/finance/invoices", { params });
    setItems(r.data);
  };

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/finance/mail/status").then((r) => setMail(r.data)).catch(() => {});
  }, [q, type]); // eslint-disable-line

  const subtotal = form.lines.reduce((a, l) => a + (Number(l.quantity || 0) * Number(l.unit_price || 0)), 0);
  const vat = subtotal * (Number(form.vat_rate) || 0) / 100;
  const total = subtotal + vat;

  const save = async () => {
    try {
      const lines = form.lines.map((l) => ({
        ...l, // keep cost_id / line_date links so edits release or adjust the right costs
        description: l.description,
        unit: l.unit || null,
        quantity: Number(l.quantity || 0),
        unit_price: Number(l.unit_price || 0),
        total: Number(l.quantity || 0) * Number(l.unit_price || 0),
      })).filter((l) => l.description);
      if (lines.length === 0) {
        toast.error("Add at least one invoice line");
        return;
      }
      const p = { ...form, lines, vat_rate: Number(form.vat_rate) };
      if (editingId) {
        await api.patch(`/finance/invoices/${editingId}`, p);
        toast.success("Invoice updated");
      } else {
        if (form.invoice_type === "customer" && Number(applyCredit) > 0) {
          p.apply_credit = Number(applyCredit);
        }
        await api.post("/finance/invoices", p);
        toast.success(Number(applyCredit) > 0 ? "Invoice created — credit applied" : "Invoice created");
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyInvoice());
      setCreditAvail(0); setApplyCredit("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openNew = () => {
    setForm(emptyInvoice());
    setEditingId(null);
    setCreditAvail(0); setApplyCredit("");
    setOpen(true);
  };

  const openEdit = (inv) => {
    setForm({
      invoice_number: inv.invoice_number || "",
      invoice_type: inv.invoice_type || "customer",
      invoice_date: inv.invoice_date || new Date().toISOString().slice(0, 10),
      due_date: inv.due_date || "",
      customer_id: inv.customer_id || "", customer_name: inv.customer_name || "",
      supplier_id: inv.supplier_id || "", supplier_name: inv.supplier_name || "",
      order_id: inv.order_id || "", order_number: inv.order_number || "",
      lines: inv.lines && inv.lines.length ? inv.lines.map((l) => ({ ...l })) : [blankLine()],
      vat_rate: inv.vat_rate ?? 15,
      currency: inv.currency || "ZAR",
      notes: inv.notes || "",
    });
    setEditingId(inv.id);
    setOpen(true);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/finance/invoices/${delItem.id}`, { params: { force: true } });
      toast.success("Invoice deleted");
      setDelItem(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const pay = async () => {
    try {
      await api.post(`/finance/invoices/${payOpen.id}/payment`, {
        amount: Number(payAmount),
        payment_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Payment recorded");
      setPayOpen(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Finance"
        subtitle="Capture costs, generate cost-plus customer invoices, payments and statements."
        actions={
          tab === "invoices" ? (
            <>
              <Btn variant="secondary" as="a" href={`${API}/exports/invoices.xlsx`} target="_blank">
                <Download className="h-4 w-4" /> Export
              </Btn>
              {can(user?.role, "finance:create") && (
                <Btn onClick={openNew} testid="new-invoice-btn">
                  <Plus className="h-4 w-4" /> New Invoice
                </Btn>
              )}
            </>
          ) : null
        }
      />

      {/* Tab switcher */}
      <div className="inline-flex rounded-sm border border-stone-300 overflow-hidden mb-4" data-testid="finance-tabs">
        {[{ k: "invoices", l: "Invoices" }, { k: "costs", l: "Costs" }, { k: "creditnotes", l: "Credit Notes" }, { k: "debitnotes", l: "Debit Notes" }].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${tab === t.k ? "bg-emerald-900 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
            data-testid={`finance-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Email sending banner */}
      {mail.configured && (
        <div className="panel px-4 py-2.5 mb-4 flex items-center gap-3 text-sm" data-testid="mail-banner">
          <Mail className="h-4 w-4 text-emerald-800" />
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
          <span>Email sending active{mail.email ? ` — invoices send from ${mail.email}` : ""}.</span>
        </div>
      )}

      {tab === "costs" ? (
        <CostsTab customers={customers} suppliers={suppliers} isAdmin={isAdmin} onInvoiced={() => { setTab("invoices"); load(); }} />
      ) : tab === "creditnotes" ? (
        <CreditNotesTab customers={customers} isAdmin={isAdmin} />
      ) : tab === "debitnotes" ? (
        <DebitNotesTab customers={customers} isAdmin={isAdmin} />
      ) : (
      <>
      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search invoice #, customer..." />
        <Select value={type} onChange={(e) => setType(e.target.value)} className="max-w-[180px]" data-testid="filter-invtype">
          <option value="">All types</option>
          {INV_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </Select>
        <div className="text-xs text-stone-500 ml-auto">{items.length} invoices</div>
      </Toolbar>

      <DataTable
        testid="invoices-table"
        columns={[
          { header: "Invoice #", mono: true, render: (r) => <span className="font-semibold">{r.invoice_number}</span> },
          { header: "Type", render: (r) => <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm bg-stone-100 text-stone-700">{r.invoice_type}</span> },
          { header: "Date", key: "invoice_date", mono: true },
          { header: "Party", render: (r) => r.customer_name || r.supplier_name || "—" },
          { header: "Order", key: "order_number" },
          { header: "Total", align: "right", mono: true, render: (r) => fmtCurrency(r.total, r.currency) },
          { header: "Paid", align: "right", mono: true, render: (r) => fmtCurrency(r.amount_paid, r.currency) },
          { header: "Balance", align: "right", mono: true, render: (r) => (
            <span className={r.total - (r.amount_paid || 0) > 0 ? "text-red-700 font-semibold" : ""}>
              {fmtCurrency(r.total - (r.amount_paid || 0), r.currency)}
            </span>
          )},
          { header: "Docs", align: "right", mono: true, render: (r) => (r.document_ids?.length || 0) },
          { header: "Status", render: (r) => <StatusBadge status={r.status} /> },
          { header: "", render: (r) => (
            <div className="flex gap-1.5 justify-end items-center">
              <a href={`${API}/finance/invoices/${r.id}/pdf`} target="_blank" rel="noreferrer" className="text-emerald-800 hover:text-emerald-900" title="Invoice PDF" data-testid={`pdf-${r.id}`} onClick={(e) => e.stopPropagation()}>
                <FileText className="h-4 w-4" />
              </a>
              <a href={`${API}/finance/invoices/${r.id}/attachments.zip`} target="_blank" rel="noreferrer" className="text-stone-600 hover:text-emerald-800" title="Download PDF + attachments (ZIP)" data-testid={`zip-${r.id}`} onClick={(e) => e.stopPropagation()}>
                <FolderArchive className="h-4 w-4" />
              </a>
              {r.invoice_type === "customer" && mail.configured && (
                <button onClick={(e) => { e.stopPropagation(); setEmailTarget(r); }}
                        className="text-blue-700 hover:text-blue-900" title="Email to customer" data-testid={`email-${r.id}`}>
                  <Mail className="h-4 w-4" />
                </button>
              )}
              {r.invoice_type === "customer" && (r.amount_paid || 0) < r.total && can(user?.role, "finance:update") && (
                <button onClick={(e) => { e.stopPropagation(); openApplyCredit(r); }}
                        className="text-violet-700 hover:text-violet-900" data-testid={`apply-credit-${r.id}`} title="Apply customer credit">
                  <Wallet className="h-4 w-4" />
                </button>
              )}
              {(r.amount_paid || 0) < r.total && can(user?.role, "finance:update") && (
                <button onClick={(e) => { e.stopPropagation(); setPayOpen(r); setPayAmount(r.total - (r.amount_paid || 0)); }}
                        className="text-blue-700 hover:text-blue-900" data-testid={`pay-${r.id}`} title="Record payment">
                  <Coins className="h-4 w-4" />
                </button>
              )}
              {can(user?.role, "finance:update") && (
                <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="text-stone-500 hover:text-emerald-800" title="Edit" data-testid={`invoice-edit-${r.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              {isAdmin && (
                <button onClick={(e) => { e.stopPropagation(); setDelItem(r); }} className="text-stone-500 hover:text-red-700" title="Delete" data-testid={`invoice-delete-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )},
        ]}
        rows={items}
      />

      {/* Statements */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="panel p-4">
          <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Receipt className="h-5 w-5 text-emerald-800" /> Customer Statements</div>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {customers.map((c) => (
              <a key={c.id} href={`${API}/finance/statements/customer/${c.id}/pdf`} target="_blank" rel="noreferrer"
                 className="flex items-center justify-between px-3 py-2 hover:bg-stone-50 rounded-sm text-sm border-b border-stone-100"
                 data-testid={`stmt-cust-${c.id}`}>
                <div>{c.name}</div>
                <div className="text-xs text-emerald-800 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> PDF</div>
              </a>
            ))}
            {customers.length === 0 && <div className="text-stone-400 text-sm py-4 text-center">No customers yet.</div>}
          </div>
        </div>
        <div className="panel p-4">
          <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Receipt className="h-5 w-5 text-emerald-800" /> Supplier Statements</div>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {suppliers.map((c) => (
              <a key={c.id} href={`${API}/finance/statements/supplier/${c.id}/pdf`} target="_blank" rel="noreferrer"
                 className="flex items-center justify-between px-3 py-2 hover:bg-stone-50 rounded-sm text-sm border-b border-stone-100"
                 data-testid={`stmt-sup-${c.id}`}>
                <div>{c.name}</div>
                <div className="text-xs text-emerald-800 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> PDF</div>
              </a>
            ))}
          </div>
        </div>
      </div>
      </>
      )}

      {/* New / Edit Invoice */}
      <Modal
        open={open} onClose={() => { setOpen(false); setEditingId(null); }} title={editingId ? "Edit Invoice" : "New Invoice"} size="xl"
        footer={<><Btn variant="secondary" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Btn><Btn onClick={save} testid="invoice-save">{editingId ? "Save Changes" : "Create Invoice"}</Btn></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <Field label="Invoice #" required>
            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="inv-number" />
          </Field>
          <Field label="Type" required>
            <Select value={form.invoice_type} onChange={(e) => setForm({ ...form, invoice_type: e.target.value })} data-testid="inv-type">
              {INV_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </Select>
          </Field>
          <Field label="Invoice Date" required>
            <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
          </Field>
          <Field label="Due Date">
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </Field>
          {form.invoice_type === "customer" ? (
            <Field label="Customer">
              <Select value={form.customer_id} onChange={(e) => {
                const c = customers.find((x) => x.id === e.target.value);
                setForm({ ...form, customer_id: e.target.value, customer_name: c?.name || "" });
                setApplyCredit("");
                loadCredit(e.target.value);
              }}>
                <option value="">—</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          ) : (
            <Field label="Supplier">
              <Select value={form.supplier_id} onChange={(e) => {
                const s = suppliers.find((x) => x.id === e.target.value);
                setForm({ ...form, supplier_id: e.target.value, supplier_name: s?.name || "" });
              }}>
                <option value="">—</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
          )}
          <Field label="Order">
            <Select value={form.order_id} onChange={(e) => {
              const o = orders.find((x) => x.id === e.target.value);
              setForm({ ...form, order_id: e.target.value, order_number: o?.order_number || "" });
            }}>
              <option value="">—</option>
              {orders.map((o) => <option key={o.id} value={o.id}>{o.order_number}</option>)}
            </Select>
          </Field>
          <Field label="VAT %">
            <Input type="number" step="0.1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} />
          </Field>
        </div>

        <div className="data-label mb-2">Line Items</div>
        <div className="space-y-2 mb-3">
          {form.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-4">
                <Input placeholder="Description" value={l.description} onChange={(e) => {
                  const lines = [...form.lines]; lines[i].description = e.target.value; setForm({ ...form, lines });
                }} data-testid={`inv-line-desc-${i}`} />
              </div>
              <div className="col-span-2">
                <Input placeholder="Unit" list="finance-units" value={l.unit || ""} onChange={(e) => {
                  const lines = [...form.lines]; lines[i].unit = e.target.value; setForm({ ...form, lines });
                }} data-testid={`inv-line-unit-${i}`} />
              </div>
              <div className="col-span-2">
                <Input type="number" step="0.001" placeholder="Qty" value={l.quantity} onChange={(e) => {
                  const lines = [...form.lines]; lines[i].quantity = e.target.value; setForm({ ...form, lines });
                }} />
              </div>
              <div className="col-span-2">
                <Input type="number" step="0.01" placeholder="Unit price" value={l.unit_price} onChange={(e) => {
                  const lines = [...form.lines]; lines[i].unit_price = e.target.value; setForm({ ...form, lines });
                }} />
              </div>
              <div className="col-span-1 text-right font-mono text-sm">
                {(Number(l.quantity || 0) * Number(l.unit_price || 0)).toFixed(2)}
              </div>
              <button className="col-span-1 text-red-700 text-xs" onClick={() => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) })}>Remove</button>
            </div>
          ))}
          <Btn variant="secondary" onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })}>+ Add Line</Btn>
        </div>
        <datalist id="finance-units">
          {UNITS.map((u) => <option key={u} value={u} />)}
        </datalist>

        <div className="border-t border-stone-200 pt-3 space-y-1 max-w-xs ml-auto text-sm">
          <div className="flex justify-between font-mono"><span className="text-stone-600">Subtotal</span><span>{fmtCurrency(subtotal, form.currency)}</span></div>
          <div className="flex justify-between font-mono"><span className="text-stone-600">VAT ({form.vat_rate}%)</span><span>{fmtCurrency(vat, form.currency)}</span></div>
          <div className="flex justify-between font-mono font-semibold text-emerald-900 border-t pt-1"><span>Total</span><span>{fmtCurrency(total, form.currency)}</span></div>
          {!editingId && form.invoice_type === "customer" && creditAvail > 0 && (
            <>
              <div className="flex justify-between items-center gap-2 text-violet-700 pt-1" data-testid="invoice-credit-row">
                <span className="text-xs">Apply Credit <span className="text-stone-500">(avail {fmtCurrency(creditAvail, form.currency)})</span></span>
                <Input type="number" step="0.01" className="w-28 text-right h-8" value={applyCredit}
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(Number(e.target.value || 0), creditAvail, total));
                    setApplyCredit(e.target.value === "" ? "" : v);
                  }}
                  placeholder="0.00" data-testid="invoice-apply-credit" />
              </div>
              <button type="button" className="text-xs text-violet-700 hover:underline w-full text-right"
                onClick={() => setApplyCredit(Math.min(creditAvail, total))} data-testid="invoice-apply-max-credit">
                Apply max ({fmtCurrency(Math.min(creditAvail, total), form.currency)})
              </button>
              <div className="flex justify-between font-mono font-semibold border-t pt-1" data-testid="invoice-balance-due">
                <span>Balance Due</span><span>{fmtCurrency(Math.max(0, total - Number(applyCredit || 0)), form.currency)}</span>
              </div>
            </>
          )}
        </div>

        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      </Modal>

      {/* Payment */}
      {payOpen && (
        <Modal
          open={!!payOpen} onClose={() => setPayOpen(null)} title={`Record Payment · ${payOpen.invoice_number}`}
          footer={<><Btn variant="secondary" onClick={() => setPayOpen(null)}>Cancel</Btn><Btn onClick={pay} testid="payment-save">Record</Btn></>}
        >
          <div className="space-y-3">
            <div className="text-sm">
              <div className="data-label">Balance Outstanding</div>
              <div className="font-mono text-lg">{fmtCurrency(payOpen.total - (payOpen.amount_paid || 0), payOpen.currency)}</div>
            </div>
            <Field label="Payment Amount">
              <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="payment-amount" />
            </Field>
            {payOpen.invoice_type === "customer" && Number(payAmount) > (payOpen.total - (payOpen.amount_paid || 0)) && (
              <div className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1.5" data-testid="payment-overpay-hint">
                Overpayment of {fmtCurrency(Number(payAmount) - (payOpen.total - (payOpen.amount_paid || 0)), payOpen.currency)} will be saved as customer credit (a credit note).
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Apply customer credit to an existing invoice */}
      {creditOpen && (
        <Modal
          open onClose={() => setCreditOpen(null)} title={`Apply Credit · ${creditOpen.invoice_number}`} testid="apply-credit-modal"
          footer={<><Btn variant="secondary" onClick={() => setCreditOpen(null)}>Cancel</Btn>
            <Btn onClick={submitApplyCredit} disabled={!(Number(creditAmt) > 0)} testid="apply-credit-submit">Apply Credit</Btn></>}>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-stone-600">Invoice total</span><span className="font-mono">{fmtCurrency(creditOpen.total, creditOpen.currency)}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">Already paid / applied</span><span className="font-mono">{fmtCurrency(creditOpen.amount_paid || 0, creditOpen.currency)}</span></div>
            <div className="flex justify-between font-semibold border-t pt-1"><span>Balance due</span><span className="font-mono">{fmtCurrency(creditOpen.total - (creditOpen.amount_paid || 0), creditOpen.currency)}</span></div>
            <div className="flex justify-between text-violet-700 font-semibold"><span>Credit available</span><span className="font-mono" data-testid="apply-credit-available">{fmtCurrency(creditInfo.available, creditOpen.currency)}</span></div>
            <Field label="Amount to apply">
              <Input type="number" step="0.01" value={creditAmt}
                onChange={(e) => {
                  const bal = creditOpen.total - (creditOpen.amount_paid || 0);
                  const v = Math.max(0, Math.min(Number(e.target.value || 0), creditInfo.available, bal));
                  setCreditAmt(e.target.value === "" ? "" : v);
                }}
                data-testid="apply-credit-amount" />
            </Field>
            {creditInfo.available <= 0 && <div className="text-xs text-stone-500" data-testid="apply-credit-none">This customer has no available credit.</div>}
            {Number(creditAmt) > 0 && (
              <div className="flex justify-between font-mono font-semibold text-emerald-900 border-t pt-1">
                <span>New balance due</span><span>{fmtCurrency(Math.max(0, creditOpen.total - (creditOpen.amount_paid || 0) - Number(creditAmt || 0)), creditOpen.currency)}</span>
              </div>
            )}
          </div>
        </Modal>
      )}


      {/* Email invoice */}
      {emailTarget && (
        <EmailInvoiceModal
          invoice={emailTarget}
          customers={customers}
          onClose={() => setEmailTarget(null)}
        />
      )}

      <ConfirmDialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        onConfirm={doDelete}
        title="Delete Invoice"
        confirmLabel="Delete Invoice"
        testid="invoice-delete-confirm"
        message={delItem ? `Delete invoice ${delItem.invoice_number}? ${(delItem.amount_paid || 0) > 0 ? `This invoice has ${fmtCurrency(delItem.amount_paid, delItem.currency)} in recorded payments which will also be removed. ` : ""}This cannot be undone.` : ""}
      />
    </AppLayout>
  );
}

// ============ EMAIL INVOICE MODAL ============
function EmailInvoiceModal({ invoice, customers, onClose }) {
  const cust = customers.find((c) => c.id === invoice.customer_id);
  const [to, setTo] = useState(cust?.email || "");
  const [subject, setSubject] = useState(`Invoice ${invoice.invoice_number}`);
  const [body, setBody] = useState(`Dear ${invoice.customer_name || "customer"},\n\nPlease find attached invoice ${invoice.invoice_number} together with the supporting cost documents.\n\nKind regards`);
  const [sending, setSending] = useState(false);
  const [includeDocs, setIncludeDocs] = useState(true);

  const send = async () => {
    if (!to.trim()) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const r = await api.post(`/finance/invoices/${invoice.id}/send-email`, { to, subject, body, include_documents: includeDocs });
      const n = r.data.emails || 1;
      toast.success(n > 1
        ? `Documents too large for one email — sending ${n} numbered emails to ${r.data.to} in the background (see Email Center for progress)`
        : `Sent to ${r.data.to} with ${r.data.attachments} attachment(s)`, { duration: 8000 });
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Email Invoice · ${invoice.invoice_number}`} size="lg" testid="email-invoice-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={send} disabled={sending} testid="email-send">{sending ? "Sending..." : "Send Email"}</Btn></>}>
      <div className="space-y-3">
        <Field label="To" required>
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" data-testid="email-to" />
        </Field>
        <Field label="Subject">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" />
        </Field>
        <Field label="Message">
          <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="email-body" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer" data-testid="email-include-docs">
          <input type="checkbox" checked={includeDocs} onChange={(e) => setIncludeDocs(e.target.checked)} />
          Attach supporting documents (bundled into one ZIP)
        </label>
        <div className="text-xs text-stone-500">
          The invoice PDF is always attached. Cost documents are zipped into a single file — if the bundle is too large for one email, it is automatically split across several numbered emails (mail 1 of N, 2 of N, …).
        </div>
      </div>
    </Modal>
  );
}

// ============ COSTS TAB ============
const emptyCost = () => ({
  cost_date: new Date().toISOString().slice(0, 10),
  category: "",
  description: "",
  supplier_id: "", supplier_name: "",
  customer_id: "", customer_name: "",
  unit: "Units",
  quantity: 1,
  unit_price: "",
  currency: "ZAR",
  notes: "",
});


const DN_STATUS_STYLE = {
  open: "bg-amber-100 text-amber-800",
  partially_paid: "bg-sky-100 text-sky-800",
  paid: "bg-emerald-100 text-emerald-800",
};

function DebitNotesTab({ customers, isAdmin }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [modal, setModal] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [payValue, setPayValue] = useState("");

  const load = async () => {
    const r = await api.get("/finance/debit-notes");
    setNotes(r.data);
  };
  useEffect(() => { load(); }, []);

  const pdf = async (n) => {
    try {
      const r = await api.get(`/finance/debit-notes/${n.id}/pdf`, { responseType: "blob" });
      saveBlob(r, `debit_note_${n.debit_note_number}.pdf`);
    } catch (e) { toast.error("Failed to generate PDF"); }
  };

  const zip = async (n) => {
    try {
      const r = await api.get(`/finance/debit-notes/${n.id}/attachments.zip`, { responseType: "blob" });
      saveBlob(r, `debit_note_${n.debit_note_number}_bundle.zip`);
    } catch (e) { toast.error("Failed to build the bundle"); }
  };

  const submitPay = async () => {
    try {
      await api.post(`/finance/debit-notes/${payTarget.id}/payment`, {
        amount: Number(payValue || 0),
        payment_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Payment recorded");
      setPayTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/finance/debit-notes/${delTarget.id}`, { params: { force: true } });
      toast.success("Debit note deleted");
      setDelTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="debitnotes-tab">
      <Toolbar>
        <div className="text-xs text-stone-500">{notes.length} debit note(s) — extra charges to customers, e.g. VAT omitted from earlier invoice lines</div>
        <div className="ml-auto">
          {can(user?.role, "finance:create") && (
            <Btn onClick={() => setModal(true)} testid="new-debit-note-btn"><Plus className="h-4 w-4" /> New Debit Note</Btn>
          )}
        </div>
      </Toolbar>

      <div className="panel overflow-hidden mt-2">
        <table className="min-w-full text-sm" data-testid="debitnotes-table">
          <thead className="bg-stone-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-xs uppercase">Date</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Debit Note #</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Customer</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Ref Invoice</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Reason</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Amount</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Paid</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Balance</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Status</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {notes.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-stone-400" data-testid="debitnotes-empty">No debit notes yet.</td></tr>
            ) : notes.map((n) => {
              const bal = Math.round(((n.amount || 0) - (n.amount_paid || 0)) * 100) / 100;
              return (
                <tr key={n.id} className="border-b" data-testid={`debitnote-row-${n.debit_note_number}`}>
                  <td className="px-3 py-2">{n.debit_date}</td>
                  <td className="px-3 py-2 font-mono font-semibold">{n.debit_note_number}</td>
                  <td className="px-3 py-2">{n.customer_name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{n.source_invoice_number || "—"}</td>
                  <td className="px-3 py-2 text-stone-600">{n.reason || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtCurrency(n.amount, n.currency)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtCurrency(n.amount_paid, n.currency)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{fmtCurrency(bal, n.currency)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${DN_STATUS_STYLE[n.status] || "bg-stone-100 text-stone-600"}`}>
                      {n.status === "paid" ? "Paid" : n.status === "partially_paid" ? "Partially Paid" : "Open"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5 justify-end">
                      <button onClick={() => pdf(n)} className="text-stone-500 hover:text-emerald-800" title="Download PDF" data-testid={`debitnote-pdf-${n.id}`}>
                        <FileText className="h-4 w-4" />
                      </button>
                      <button onClick={() => zip(n)} className="text-stone-500 hover:text-emerald-800" title="Download PDF + cost attachments (ZIP)" data-testid={`debitnote-zip-${n.id}`}>
                        <FolderArchive className="h-4 w-4" />
                      </button>
                      {can(user?.role, "finance:create") && (
                        <button onClick={() => setEmailTarget(n)} className="text-stone-500 hover:text-emerald-800" title="Email to customer" data-testid={`debitnote-email-${n.id}`}>
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                      {can(user?.role, "finance:update") && bal > 0 && (
                        <button onClick={() => { setPayTarget(n); setPayValue(String(bal)); }} className="text-violet-700 hover:text-violet-900" title="Record customer payment" data-testid={`debitnote-pay-${n.id}`}>
                          <Coins className="h-4 w-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => setDelTarget(n)} className="text-stone-500 hover:text-red-700" title="Delete" data-testid={`debitnote-delete-${n.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <DebitNoteModal customers={customers} onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />
      )}
      {emailTarget && (
        <EmailDebitNoteModal note={emailTarget} customers={customers} onClose={() => setEmailTarget(null)} />
      )}
      {payTarget && (
        <Modal open onClose={() => setPayTarget(null)} title="Record Customer Payment" size="sm" testid="debitnote-pay-modal"
          footer={<><Btn variant="secondary" onClick={() => setPayTarget(null)}>Cancel</Btn>
            <Btn onClick={submitPay} testid="debitnote-pay-save">Record Payment</Btn></>}>
          <div className="text-sm mb-2">{payTarget.debit_note_number} · {payTarget.customer_name} · balance {fmtCurrency((payTarget.amount || 0) - (payTarget.amount_paid || 0), payTarget.currency)}</div>
          <Field label="Amount received (ZAR)">
            <Input type="number" step="0.01" value={payValue} onChange={(e) => setPayValue(e.target.value)} data-testid="debitnote-pay-amount" />
          </Field>
        </Modal>
      )}
      <ConfirmDialog
        open={!!delTarget}
        onCancel={() => setDelTarget(null)}
        onConfirm={doDelete}
        title="Delete debit note?"
        message={delTarget ? `Delete debit note ${delTarget.debit_note_number} (${fmtCurrency(delTarget.amount, delTarget.currency)})? This cannot be undone.` : ""}
      />
    </div>
  );
}

function EmailDebitNoteModal({ note, customers, onClose }) {
  const cust = customers.find((c) => c.id === note.customer_id);
  const [to, setTo] = useState(cust?.email || "");
  const [subject, setSubject] = useState(`Debit Note ${note.debit_note_number}`);
  const [body, setBody] = useState(`Dear ${note.customer_name || "customer"},\n\nPlease find attached debit note ${note.debit_note_number}${note.source_invoice_number ? ` relating to invoice ${note.source_invoice_number}` : ""}, together with the supporting cost documents.\n\nKind regards`);
  const [sending, setSending] = useState(false);
  const [includeDocs, setIncludeDocs] = useState(true);

  const send = async () => {
    if (!to.trim()) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const r = await api.post(`/finance/debit-notes/${note.id}/send-email`, { to, subject, body, include_documents: includeDocs });
      toast.success(`Sent to ${r.data.to} with ${r.data.attachments} attachment(s)`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Email Debit Note · ${note.debit_note_number}`} size="lg" testid="email-debitnote-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={send} disabled={sending} testid="debitnote-email-send">{sending ? "Sending..." : "Send Email"}</Btn></>}>
      <div className="space-y-3">
        <Field label="To" required>
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" data-testid="debitnote-email-to" />
        </Field>
        <Field label="Subject">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="debitnote-email-subject" />
        </Field>
        <Field label="Message">
          <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="debitnote-email-body" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer" data-testid="debitnote-email-include-docs">
          <input type="checkbox" checked={includeDocs} onChange={(e) => setIncludeDocs(e.target.checked)} />
          Attach supporting documents (bundled into one ZIP)
        </label>
        <div className="text-xs text-stone-500">
          The debit note PDF is always attached. Matching cost documents are zipped into a single file.
        </div>
      </div>
    </Modal>
  );
}

function DebitNoteModal({ customers, onClose, onSaved }) {
  const [form, setForm] = useState({ customer_id: "", debit_date: new Date().toISOString().slice(0, 10), reason: "VAT omitted from original invoice", notes: "" });
  const [invoices, setInvoices] = useState([]);
  const [invId, setInvId] = useState("");
  const [lines, setLines] = useState([]); // {checked, description, base_amount, vat_rate, amount}
  const [saving, setSaving] = useState(false);

  const inv = invoices.find((i) => i.id === invId);

  const loadInvoices = async (cid) => {
    if (!cid) { setInvoices([]); return; }
    const r = await api.get("/finance/invoices", { params: { customer_id: cid, invoice_type: "customer" } });
    setInvoices(r.data.filter((i) => i.status !== "cancelled"));
  };

  const pickInvoice = (id) => {
    setInvId(id);
    const i = invoices.find((x) => x.id === id);
    const rate = Number(i?.vat_rate ?? 15) || 15;
    setLines((i?.lines || []).map((l) => {
      const excl = Number(l.total) || 0;
      return {
        checked: false,
        description: l.description,
        original_excl: excl,
        original_vat: Math.round(excl * rate) / 100,
        correct_excl: excl,
        vat_rate: rate,
      };
    }));
  };

  const derived = (l) => {
    const cVat = Math.round((Number(l.correct_excl) || 0) * (Number(l.vat_rate) || 0)) / 100;
    const amount = Math.round(((Number(l.correct_excl) || 0) + cVat
      - (Number(l.original_excl) || 0) - (Number(l.original_vat) || 0)) * 100) / 100;
    return { correct_vat: cVat, amount };
  };

  const setLine = (i, patch) => setLines(lines.map((l, li) => (li === i ? { ...l, ...patch } : l)));

  const chosen = lines.filter((l) => l.checked && derived(l).amount > 0);
  const total = chosen.reduce((a, l) => a + derived(l).amount, 0);

  const save = async () => {
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!chosen.length) { toast.error("Tick at least one invoice line to charge"); return; }
    setSaving(true);
    try {
      const c = customers.find((x) => x.id === form.customer_id);
      const r = await api.post("/finance/debit-notes", {
        customer_id: form.customer_id,
        customer_name: c?.name || null,
        debit_date: form.debit_date,
        source_invoice_id: invId || null,
        reason: form.reason || null,
        notes: form.notes || null,
        lines: chosen.map((l) => {
          const d = derived(l);
          return {
            description: l.description,
            base_amount: Number(l.original_excl) || 0,
            original_excl: Number(l.original_excl) || 0,
            original_vat: Number(l.original_vat) || 0,
            correct_excl: Number(l.correct_excl) || 0,
            correct_vat: d.correct_vat,
            vat_rate: Number(l.vat_rate) || 0,
            amount: d.amount,
          };
        }),
      });
      toast.success(`Debit note ${r.data.debit_note_number} raised — ${fmtCurrency(r.data.amount, r.data.currency)}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="New Debit Note — charge missing VAT on invoice lines" size="xl" testid="debit-note-modal"
      footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving || !chosen.length} testid="debitnote-save">
          {saving ? "Saving..." : `Raise Debit Note (${fmtCurrency(total, "ZAR")})`}
        </Btn></>}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Field label="Customer" required>
          <Select value={form.customer_id} onChange={(e) => {
            setForm({ ...form, customer_id: e.target.value });
            setInvId(""); setLines([]);
            loadInvoices(e.target.value);
          }} data-testid="debitnote-customer">
            <option value="">—</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Source Invoice" required>
          <Select value={invId} onChange={(e) => pickInvoice(e.target.value)} disabled={!form.customer_id} data-testid="debitnote-invoice">
            <option value="">—</option>
            {invoices.map((i) => <option key={i.id} value={i.id}>{i.invoice_number} · {i.invoice_date} · {fmtCurrency(i.total, i.currency)}</option>)}
          </Select>
        </Field>
        <Field label="Debit Date">
          <Input type="date" value={form.debit_date} onChange={(e) => setForm({ ...form, debit_date: e.target.value })} data-testid="debitnote-date" />
        </Field>
      </div>

      {inv && (
        <div className="panel overflow-x-auto mb-4" data-testid="debitnote-lines">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-100 border-b">
              <tr>
                <th className="px-2 py-2"></th>
                <th className="px-2 py-2 text-left text-xs uppercase">Invoice Line</th>
                <th className="px-2 py-2 text-right text-xs uppercase">Invoiced (ex VAT)</th>
                <th className="px-2 py-2 text-right text-xs uppercase">Invoiced VAT</th>
                <th className="px-2 py-2 text-right text-xs uppercase">Should Be (ex VAT)</th>
                <th className="px-2 py-2 text-right text-xs uppercase">VAT %</th>
                <th className="px-2 py-2 text-right text-xs uppercase">Should Be VAT</th>
                <th className="px-2 py-2 text-right text-xs uppercase">To Charge</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const d = derived(l);
                return (
                  <tr key={i} className={`border-b ${l.checked ? "bg-amber-50/60" : ""}`} data-testid={`debitnote-line-${i}`}>
                    <td className="px-2 py-2">
                      <input type="checkbox" checked={l.checked} onChange={(e) => setLine(i, { checked: e.target.checked })} data-testid={`debitnote-line-check-${i}`} />
                    </td>
                    <td className="px-2 py-2">{l.description}</td>
                    <td className="px-2 py-2 text-right font-mono">{fmtCurrency(l.original_excl, "ZAR")}</td>
                    <td className="px-2 py-2 text-right">
                      <Input type="number" step="0.01" className="w-24 h-7 text-right font-mono inline-block" value={l.original_vat}
                        onChange={(e) => setLine(i, { original_vat: e.target.value })} data-testid={`debitnote-line-origvat-${i}`} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Input type="number" step="0.01" className="w-28 h-7 text-right font-mono inline-block" value={l.correct_excl}
                        onChange={(e) => setLine(i, { correct_excl: e.target.value })} data-testid={`debitnote-line-correct-${i}`} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Input type="number" step="0.5" className="w-16 h-7 text-right font-mono inline-block" value={l.vat_rate}
                        onChange={(e) => setLine(i, { vat_rate: e.target.value })} data-testid={`debitnote-line-rate-${i}`} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono">{fmtCurrency(d.correct_vat, "ZAR")}</td>
                    <td className={`px-2 py-2 text-right font-mono font-semibold ${d.amount > 0 ? "text-amber-800" : "text-stone-400"}`} data-testid={`debitnote-line-amount-${i}`}>
                      {fmtCurrency(d.amount, "ZAR")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-stone-50 font-semibold">
                <td className="px-2 py-2" colSpan={7}>Total to charge ({chosen.length} line(s)) — (should-be incl. VAT) minus (invoiced incl. VAT)</td>
                <td className="px-2 py-2 text-right font-mono" data-testid="debitnote-total">{fmtCurrency(total, "ZAR")}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Reason (appears on the PDF)">
          <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="debitnote-reason" />
        </Field>
        <Field label="Notes">
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="debitnote-notes" />
        </Field>
      </div>
    </Modal>
  );
}


function CreditNotesTab({ customers, isAdmin }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [modal, setModal] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [form, setForm] = useState({ customer_id: "", credit_date: new Date().toISOString().slice(0, 10), amount: "", reason: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/finance/credit-notes");
    setNotes(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Enter an amount greater than zero"); return; }
    setSaving(true);
    try {
      const c = customers.find((x) => x.id === form.customer_id);
      await api.post("/finance/credit-notes", {
        customer_id: form.customer_id,
        customer_name: c?.name || null,
        credit_date: form.credit_date,
        amount: Number(form.amount),
        reason: form.reason || null,
        notes: form.notes || null,
      });
      toast.success("Credit note raised");
      setModal(false);
      setForm({ customer_id: "", credit_date: new Date().toISOString().slice(0, 10), amount: "", reason: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/finance/credit-notes/${delTarget.id}`, { params: { force: true } });
      toast.success("Credit note deleted");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const STATUS_STYLE = {
    open: "bg-emerald-100 text-emerald-800",
    partially_applied: "bg-amber-100 text-amber-800",
    applied: "bg-stone-200 text-stone-600",
  };

  return (
    <div data-testid="creditnotes-tab">
      <Toolbar>
        <div className="text-xs text-stone-500">{notes.length} credit note(s)</div>
        <div className="ml-auto">
          {can(user?.role, "finance:create") && (
            <Btn onClick={() => setModal(true)} testid="new-credit-note-btn"><Plus className="h-4 w-4" /> New Credit Note</Btn>
          )}
        </div>
      </Toolbar>

      <div className="panel overflow-hidden mt-2">
        <table className="min-w-full text-sm" data-testid="creditnotes-table">
          <thead className="bg-stone-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-xs uppercase">Date</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Credit Note #</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Customer</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Reason</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Source</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Amount</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Applied</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Remaining</th>
              <th className="px-3 py-2 text-left text-xs uppercase">Status</th>
              <th className="px-3 py-2 text-right text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {notes.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-stone-400" data-testid="creditnotes-empty">No credit notes yet.</td></tr>
            ) : notes.map((n) => {
              const rem = Math.round(((n.amount || 0) - (n.applied_amount || 0)) * 100) / 100;
              return (
                <tr key={n.id} className="border-b" data-testid={`creditnote-row-${n.credit_note_number}`}>
                  <td className="px-3 py-2">{n.credit_date}</td>
                  <td className="px-3 py-2 font-mono font-semibold">{n.credit_note_number}</td>
                  <td className="px-3 py-2">{n.customer_name || "—"}</td>
                  <td className="px-3 py-2 text-stone-600">{n.reason || "—"}</td>
                  <td className="px-3 py-2 text-xs">{n.source === "overpayment" ? "Overpayment" : "Manual"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtCurrency(n.amount, n.currency)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtCurrency(n.applied_amount, n.currency)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-800">{fmtCurrency(rem, n.currency)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-sm ${STATUS_STYLE[n.status] || "bg-stone-100"}`}>
                      {(n.status || "open").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <a href={`${API}/finance/credit-notes/${n.id}/pdf`} target="_blank" rel="noreferrer"
                       className="inline-flex text-stone-600 hover:text-emerald-800" title="Download PDF" data-testid={`creditnote-pdf-${n.credit_note_number}`}>
                      <FileText className="h-4 w-4" />
                    </a>
                    {isAdmin && (n.applied_amount || 0) === 0 && (
                      <button onClick={() => setDelTarget(n)} className="ml-3 text-red-700 hover:text-red-900" title="Delete" data-testid={`creditnote-delete-${n.credit_note_number}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal open onClose={() => setModal(false)} title="Raise Credit Note" testid="credit-note-modal"
          footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} disabled={saving} testid="credit-note-save">Raise Credit Note</Btn></>}>
          <div className="space-y-4">
            <Field label="Customer" required>
              <Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="cn-customer">
                <option value="">—</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date" required>
                <Input type="date" value={form.credit_date} onChange={(e) => setForm({ ...form, credit_date: e.target.value })} data-testid="cn-date" />
              </Field>
              <Field label="Amount (ZAR)" required>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cn-amount" />
              </Field>
            </div>
            <Field label="Reason">
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Goods returned, discount agreed" data-testid="cn-reason" />
            </Field>
            <Field label="Notes">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cn-notes" />
            </Field>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!delTarget}
        onClose={() => setDelTarget(null)}
        onConfirm={doDelete}
        title="Delete Credit Note"
        confirmLabel="Delete"
        testid="credit-note-delete-confirm"
        message={delTarget ? `Delete credit note ${delTarget.credit_note_number} (${fmtCurrency(delTarget.amount, delTarget.currency)})? This cannot be undone.` : ""}
      />
    </div>
  );
}



function CostsTab({ customers, suppliers, isAdmin, onInvoiced }) {
  const { user } = useAuth();
  const [costs, setCosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [payStatusF, setPayStatusF] = useState("");
  const [catF, setCatF] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [costModal, setCostModal] = useState(null); // {} new or cost edit
  const [tagTarget, setTagTarget] = useState(null); // cost being tagged (split)
  const [docTarget, setDocTarget] = useState(null);
  const [catModal, setCatModal] = useState(false);
  const [invModal, setInvModal] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [allocTarget, setAllocTarget] = useState(null); // cost being split across dates
  const [payValue, setPayValue] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const canCreate = can(user?.role, "finance:create");

  const openPay = (r) => {
    setPayTarget(r);
    setPayValue(r.paid_amount ? String(r.paid_amount) : String(r.amount || ""));
  };
  const submitPay = async () => {
    try {
      await api.post(`/finance/costs/${payTarget.id}/payment`, { paid_amount: Number(payValue || 0) });
      toast.success("Cost payment recorded");
      setPayTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (statusF) params.status = statusF;
    if (payStatusF) params.payment_status = payStatusF;
    if (catF) params.category = catF;
    const [c, cat] = await Promise.all([
      api.get("/finance/costs", { params }),
      api.get("/finance/cost-categories"),
    ]);
    setCosts(c.data);
    setCategories(cat.data);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, [q, statusF, payStatusF, catF]); // eslint-disable-line

  const toggle = (id) => setSelected((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectedCosts = costs.filter((c) => selected.has(c.id));
  const selectedUninvoiced = selectedCosts.filter((c) => c.status === "uninvoiced" || c.status === "partial");
  const selTotal = selectedUninvoiced.reduce((a, c) => a + remainingOf(c), 0);

  const exportXlsx = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (statusF) params.status = statusF;
      if (payStatusF) params.payment_status = payStatusF;
      if (catF) params.category = catF;
      const r = await api.get("/finance/costs.xlsx", { params, responseType: "blob" });
      saveBlob(r, "costs.xlsx");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/finance/costs/${delTarget.id}`, { params: { force: true } });
      toast.success("Cost deleted");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search cost, category, supplier..." testid="cost-search" />
        <Select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="max-w-[160px]" data-testid="cost-filter-status">
          <option value="">All invoice statuses</option>
          <option value="uninvoiced">Uninvoiced</option>
          <option value="partial">Partially Invoiced</option>
          <option value="invoiced">Invoiced</option>
          <option value="paid">Invoice Paid</option>
        </Select>
        <Select value={payStatusF} onChange={(e) => setPayStatusF(e.target.value)} className="max-w-[170px]" data-testid="cost-filter-payment">
          <option value="">All supplier payments</option>
          <option value="unpaid">Supplier: Unpaid</option>
          <option value="partial">Supplier: Partial</option>
          <option value="paid">Supplier: Paid</option>
        </Select>
        <Select value={catF} onChange={(e) => setCatF(e.target.value)} className="max-w-[160px]" data-testid="cost-filter-category">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </Select>
        <Btn variant="secondary" onClick={() => setCatModal(true)} testid="manage-categories-btn">
          <Tags className="h-4 w-4" /> Categories
        </Btn>
        <Btn variant="secondary" onClick={exportXlsx} testid="export-costs-btn">
          <Download className="h-4 w-4" /> Excel
        </Btn>
        {canCreate && (
          <Btn onClick={() => setCostModal(emptyCost())} testid="new-cost-btn">
            <Plus className="h-4 w-4" /> New Cost
          </Btn>
        )}
        {canCreate && (
          <Btn variant="secondary" onClick={() => setImportOpen(true)} testid="import-costs-btn">
            <Upload className="h-4 w-4" /> Import Excel
          </Btn>
        )}
        {canCreate && (
          <Btn variant="secondary" onClick={() => setAttachOpen(true)} testid="bulk-attach-btn">
            <Paperclip className="h-4 w-4" /> Bulk Attach
          </Btn>
        )}
        {canCreate && (
          <Btn variant="secondary" testid="generate-recurring-btn" onClick={async () => {
            try {
              const r = await api.post("/finance/costs/generate-recurring", { month: new Date().toISOString().slice(0, 7) });
              toast.success(r.data.created ? `${r.data.created} recurring cost(s) added for this month` : "Nothing to add — recurring costs already captured this month");
              load();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }}>
            <RefreshCw className="h-4 w-4" /> Recurring → This Month
          </Btn>
        )}
        <div className="text-xs text-stone-500 ml-auto">{costs.length} costs</div>
      </Toolbar>

      <CostImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={() => load()} />
      <BulkAttachModal open={attachOpen} onClose={() => setAttachOpen(false)} onDone={() => load()} />

      {selectedUninvoiced.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 panel px-4 py-2.5 bg-emerald-50/60 border-emerald-200" data-testid="cost-selection-bar">
          <span className="text-sm font-medium text-emerald-900">
            {selectedUninvoiced.length} cost(s) selected · {fmtCurrency(selTotal, "ZAR")}
          </span>
          <Btn onClick={() => setInvModal(true)} testid="create-invoice-from-costs-btn">
            <Receipt className="h-4 w-4" /> Create Customer Invoice
          </Btn>
          <button onClick={() => setSelected(new Set())} className="text-xs text-stone-500 hover:text-stone-800 ml-auto" data-testid="cost-clear-selection">
            Clear selection
          </button>
        </div>
      )}

      <DataTable
        testid="costs-table"
        columns={[
          {
            header: "",
            render: (r) => (
              (r.status === "uninvoiced" || r.status === "partial")
                ? <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`cost-select-${r.id}`} />
                : <span className="text-stone-300">—</span>
            ),
          },
          { header: "Date", mono: true, render: (r) => (
            (r.date_allocations?.length || 0) > 0 ? (
              <span className="inline-flex flex-col gap-0.5" data-testid={`cost-date-split-${r.id}`}>
                <span>{r.cost_date}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-sky-50 text-sky-800 border border-sky-200 whitespace-nowrap"
                      title={r.date_allocations.map((a) => `${a.date} · ${fmtCurrency(a.amount, r.currency)}`).join("\n")}>
                  split × {r.date_allocations.length} dates
                </span>
              </span>
            ) : r.cost_date
          )},
          { header: "Category", render: (r) => <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm bg-stone-100 text-stone-700">{r.category}</span> },
          { header: "Class", render: (r) => {
            const sp = amountsOf(r);
            const full = Number(r.amount) || 0;
            return (
              <span className="inline-flex items-center gap-1 flex-wrap">
                {sp ? Object.entries(sp).map(([k, v]) => (
                  <span key={k} className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${CLASS_META[k]?.cls || "bg-stone-100 text-stone-700"}`}>
                    {CLASS_META[k]?.label || k}{Math.abs(Number(v) - full) < 0.01 ? "" : ` ${fmtCurrency(Number(v), "ZAR")}`}
                  </span>
                )) : <span className="text-stone-300">—</span>}
                {r.recurring && <span className="text-[10px] text-emerald-800 font-semibold" title="Recurring monthly">↻</span>}
              </span>
            );
          }},
          { header: "Description", render: (r) => r.description },
          { header: "Supplier", render: (r) => r.supplier_name || "—" },
          { header: "Customer", render: (r) => r.customer_name || "—" },
          { header: "Qty", align: "right", mono: true, render: (r) => (r.quantity ?? "—") },
          { header: "Unit", render: (r) => r.unit || "—" },
          { header: "Rate", align: "right", mono: true, render: (r) => fmtCurrency(r.unit_price, r.currency) },
          { header: "Amount", align: "right", mono: true, render: (r) => fmtCurrency(r.amount, r.currency) },
          { header: "Docs", align: "right", render: (r) => (
            <button onClick={() => setDocTarget(r)} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-emerald-800" title="Attachments" data-testid={`cost-docs-${r.id}`}>
              <Paperclip className="h-3.5 w-3.5" /><span className="font-mono">{r.document_ids?.length || 0}</span>
            </button>
          )},
          { header: "Invoice", render: (r) => (
            r.status === "paid"
              ? <span className="text-xs px-2 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-200" title={`Customer paid invoice ${r.invoice_number}`}>Invoice Paid</span>
              : r.status === "invoiced"
                ? <span className="text-xs px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-800 border border-indigo-200" title={`On invoice ${r.invoice_number}`}>Invoiced</span>
                : r.status === "partial"
                  ? <span className="inline-flex flex-col items-start gap-0.5" data-testid={`cost-partial-${r.id}`}>
                      <span className="text-xs px-2 py-0.5 rounded-sm bg-orange-50 text-orange-800 border border-orange-200" title={`${fmtCurrency(r.invoiced_amount, r.currency)} invoiced so far (last: ${r.invoice_number})`}>Partially invoiced</span>
                      <span className="text-[10px] font-mono text-orange-700 whitespace-nowrap">{fmtCurrency(remainingOf(r), r.currency)} to invoice</span>
                    </span>
                  : <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200">Uninvoiced</span>
          )},
          { header: "Supplier Paid", align: "right", render: (r) => {
            const paid = Number(r.paid_amount || 0);
            const amt = Number(r.amount || 0);
            const st = paid >= amt - 0.001 && amt > 0 ? "paid" : paid > 0 ? "partial" : "unpaid";
            const style = st === "paid" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : st === "partial" ? "bg-amber-50 text-amber-800 border-amber-200"
              : "bg-stone-50 text-stone-600 border-stone-200";
            return (
              <div className="flex flex-col items-end gap-0.5" data-testid={`cost-paid-${r.id}`}>
                <span className="font-mono text-xs">{fmtCurrency(paid, r.currency)}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${style}`}>{st === "paid" ? "Paid" : st === "partial" ? "Partial" : "Unpaid"}</span>
              </div>
            );
          }},
          { header: "", align: "right", render: (r) => (
            <div className="flex gap-1.5 justify-end">
              {can(user?.role, "finance:update") && (
                <button onClick={() => setTagTarget(r)} className="text-emerald-800 hover:text-emerald-950" title="Tag Start-up / Variable / Fee (works on past & invoiced costs)" data-testid={`cost-tag-${r.id}`}>
                  <Tag className="h-4 w-4" />
                </button>
              )}
              {can(user?.role, "finance:update") && (
                <button onClick={() => setAllocTarget(r)} className="text-sky-700 hover:text-sky-900" title="Split across supplier invoice dates (works on past & invoiced costs)" data-testid={`cost-alloc-${r.id}`}>
                  <CalendarDays className="h-4 w-4" />
                </button>
              )}
              {can(user?.role, "finance:update") && (
                <button onClick={() => openPay(r)} className="text-violet-700 hover:text-violet-900" title="Record supplier payment" data-testid={`cost-pay-${r.id}`}>
                  <Coins className="h-4 w-4" />
                </button>
              )}
              {canCreate && r.status === "uninvoiced" && (
                <button onClick={() => setCostModal({ ...r })} className="text-stone-500 hover:text-emerald-800" title="Edit" data-testid={`cost-edit-${r.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              {can(user?.role, "finance:delete") && (
                <button onClick={() => setDelTarget(r)} className="text-stone-500 hover:text-red-700" title="Delete" data-testid={`cost-delete-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )},
        ]}
        rows={costs}
        empty="No costs captured yet."
      />

      {payTarget && (
        <Modal
          open onClose={() => setPayTarget(null)} title="Record Supplier Payment" testid="cost-pay-modal"
          footer={<><Btn variant="secondary" onClick={() => setPayTarget(null)}>Cancel</Btn>
            <Btn onClick={submitPay} testid="cost-pay-submit">Save</Btn></>}>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-stone-600">Cost</span><span className="font-medium">{payTarget.description}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">Supplier</span><span>{payTarget.supplier_name || "—"}</span></div>
            <div className="flex justify-between font-semibold border-t pt-1"><span>Cost amount</span><span className="font-mono">{fmtCurrency(payTarget.amount, payTarget.currency)}</span></div>
            <Field label="Amount paid to supplier">
              <Input type="number" step="0.01" value={payValue} onChange={(e) => setPayValue(e.target.value)} data-testid="cost-pay-amount" />
            </Field>
            <div className="flex items-center justify-between">
              <button type="button" className="text-xs text-violet-700 hover:underline" onClick={() => setPayValue(String(payTarget.amount || 0))} data-testid="cost-pay-mark-full">
                Mark fully paid ({fmtCurrency(payTarget.amount, payTarget.currency)})
              </button>
              {Number(payValue || 0) < Number(payTarget.amount || 0) && Number(payValue || 0) > 0 && (
                <span className="text-xs text-amber-700">Outstanding to supplier: {fmtCurrency(Number(payTarget.amount || 0) - Number(payValue || 0), payTarget.currency)}</span>
              )}
            </div>
          </div>
        </Modal>
      )}
      {costModal && (
        <CostModal
          initial={costModal}
          categories={categories}
          suppliers={suppliers}
          customers={customers}
          onClose={() => { setCostModal(null); load(); }}
          onSavedNew={(cost) => { setCostModal(null); load(); setDocTarget(cost); }}
        />
      )}
      {tagTarget && (
        <TagModal cost={tagTarget} onClose={() => { setTagTarget(null); load(); }} />
      )}
      {allocTarget && (
        <DateAllocationsModal cost={allocTarget} onClose={() => { setAllocTarget(null); load(); }} />
      )}
      {docTarget && (
        <CostDocsModal cost={docTarget} onClose={() => { setDocTarget(null); load(); }} />
      )}
      {catModal && (
        <CategoriesModal categories={categories} isAdmin={isAdmin} onClose={() => { setCatModal(false); load(); }} />
      )}
      {invModal && (
        <CreateInvoiceModal
          costs={selectedUninvoiced}
          customers={customers}
          onClose={() => setInvModal(false)}
          onCreated={() => { setInvModal(false); onInvoiced(); }}
        />
      )}
      <ConfirmDialog
        open={!!delTarget}
        onClose={() => setDelTarget(null)}
        onConfirm={doDelete}
        title="Delete Cost"
        confirmLabel="Delete Cost"
        testid="cost-delete-confirm"
        message={delTarget ? `Delete this ${delTarget.category} cost (${fmtCurrency(delTarget.amount, delTarget.currency)})?${(delTarget.status === "invoiced" || delTarget.status === "partial") ? ` It is on invoice ${delTarget.invoice_number} and will be unlinked.` : ""}` : ""}
      />
    </>
  );
}

// ============ COST MODAL ============
// ============ TARIFF CLASSIFICATION (Start-up / Variable / Fee splits) ============
const CLASS_META = {
  startup: { label: "Start-up", cls: "bg-amber-100 text-amber-800" },
  variable: { label: "Variable", cls: "bg-blue-100 text-blue-800" },
  fixed: { label: "Fixed", cls: "bg-purple-100 text-purple-800" },
  consumables: { label: "Consumables", cls: "bg-orange-100 text-orange-800" },
  transport: { label: "Transport", cls: "bg-cyan-100 text-cyan-800" },
  fee: { label: "Fee", cls: "bg-emerald-100 text-emerald-900" },
};
const CLASS_KEYS = Object.keys(CLASS_META);
const splitOf = (r) => r?.class_split || (r?.cost_class ? { [r.cost_class]: 100 } : null);
const amountsOf = (r) => {
  if (r?.class_split_amounts) return { ...r.class_split_amounts };
  const amt = Number(r?.amount) || 0;
  if (r?.class_split) {
    const out = {};
    Object.entries(r.class_split).forEach(([k, p]) => { out[k] = Math.round(amt * Number(p)) / 100; });
    return out;
  }
  if (r?.cost_class) return { [r.cost_class]: amt };
  return null;
};
const splitTotal = (sp) => CLASS_KEYS.reduce((a, k) => a + (Number(sp?.[k]) || 0), 0);

function classPayload(split, amount) {
  const out = {};
  CLASS_KEYS.forEach((k) => { const v = Number(split?.[k]) || 0; if (v > 0) out[k] = Math.round(v * 100) / 100; });
  const keys = Object.keys(out);
  return {
    class_split_amounts: keys.length ? out : null,
    class_split: null,
    cost_class: keys.length === 1 && Math.abs(out[keys[0]] - (Number(amount) || 0)) < 0.01 ? keys[0] : null,
  };
}

function ClassSplitEditor({ value, onChange, amount }) {
  const v = value || {};
  const set = (k, val) => {
    const next = { ...v };
    if (val === "" || Number(val) === 0) delete next[k];
    else next[k] = Number(val);
    onChange(next);
  };
  const total = splitTotal(v);
  const amt = Number(amount) || 0;
  const over = total > amt + 0.01;
  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {CLASS_KEYS.map((k) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">{CLASS_META[k].label} (R)</div>
            <div className="flex gap-1">
              <input type="number" min="0" step="0.01" value={v[k] ?? ""} onChange={(e) => set(k, e.target.value)}
                     className="w-full px-2 py-1.5 border border-stone-300 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-800/30"
                     data-testid={`split-${k}`} />
              <button type="button" className="text-[10px] px-1.5 border border-stone-200 rounded-sm text-stone-500 hover:bg-stone-50"
                      title={`Full amount to ${CLASS_META[k].label}`} onClick={() => onChange({ [k]: amt })} data-testid={`split-all-${k}`}>All</button>
            </div>
          </div>
        ))}
      </div>
      <div className={`text-xs mt-1.5 ${over ? "text-red-600 font-semibold" : "text-stone-500"}`} data-testid="split-total">
        Tagged: {fmtCurrency(total, "ZAR")} of {fmtCurrency(amt, "ZAR")}
        {over ? " — cannot exceed the cost amount" : total > 0 && amt - total > 0.01 ? ` · ${fmtCurrency(Math.round((amt - total) * 100) / 100, "ZAR")} untagged` : ""}
      </div>
    </div>
  );
}

function TagModal({ cost, onClose }) {
  const [split, setSplit] = useState(amountsOf(cost) || {});
  const [saving, setSaving] = useState(false);
  const amt = Number(cost.amount) || 0;
  const save = async () => {
    if (splitTotal(split) > amt + 0.01) { toast.error("Tagged amounts cannot exceed the cost amount"); return; }
    setSaving(true);
    try {
      await api.patch(`/finance/costs/${cost.id}`, classPayload(split, amt));
      toast.success("Tariff tags saved");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title="Tag Cost — Tariff Classification" size="md" testid="tag-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={save} disabled={saving} testid="tag-save">{saving ? "Saving..." : "Save Tags"}</Btn></>}>
      <div className="text-sm font-medium mb-1">{cost.description}</div>
      <div className="text-xs text-stone-500 mb-3">
        {cost.cost_date} · {fmtCurrency(cost.amount, cost.currency)}{cost.invoice_number ? ` · on invoice ${cost.invoice_number}` : ""} —
        tagging works on any cost, including past and invoiced ones.
      </div>
      <ClassSplitEditor value={split} onChange={setSplit} amount={amt} />
    </Modal>
  );
}


function AllocationsEditor({ rows, setRows, amount, currency = "ZAR" }) {
  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const remaining = Math.round((amount - total) * 100) / 100;
  const balanced = Math.abs(remaining) < 0.01;
  return (
    <div data-testid="date-allocations-editor">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`alloc-row-${i}`}>
          <Input type="date" className="w-40" value={r.date}
            onChange={(e) => setRows(rows.map((x, xi) => xi === i ? { ...x, date: e.target.value } : x))}
            data-testid={`alloc-date-${i}`} />
          <Input type="number" step="0.01" className="w-32" value={r.amount} placeholder="Amount"
            onChange={(e) => setRows(rows.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))}
            data-testid={`alloc-amount-${i}`} />
          <button type="button" className="text-xs text-red-600 hover:underline"
            onClick={() => setRows(rows.filter((_, xi) => xi !== i))} data-testid={`alloc-remove-${i}`}>Remove</button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 mt-1">
        <button type="button" className="text-xs text-emerald-700 hover:underline"
          onClick={() => setRows([...rows, { date: "", amount: remaining > 0 ? String(remaining) : "" }])}
          data-testid="alloc-add">+ Add date</button>
        {rows.length > 0 && (
          <span className={`text-xs font-mono ${balanced ? "text-emerald-700" : "text-red-600 font-semibold"}`} data-testid="alloc-balance">
            allocated {fmtCurrency(total, currency)} of {fmtCurrency(amount, currency)}
            {balanced ? " ✓" : ` — ${remaining > 0 ? fmtCurrency(remaining, currency) + " left to allocate" : fmtCurrency(-remaining, currency) + " over"}`}
          </span>
        )}
      </div>
    </div>
  );
}

const allocPayload = (rows) => {
  const clean = rows
    .filter((r) => r.date && Number(r.amount) > 0)
    .map((r) => ({ date: r.date, amount: Number(r.amount) }));
  return clean.length ? clean : null;
};

function DateAllocationsModal({ cost, onClose }) {
  const [rows, setRows] = useState((cost.date_allocations || []).map((a) => ({ date: a.date, amount: String(a.amount) })));
  const [saving, setSaving] = useState(false);
  const amount = Number(cost.amount) || 0;
  const save = async () => {
    const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
    if (rows.length && Math.abs(total - amount) > 0.01) {
      toast.error("Allocated dates must add up to the full cost amount"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/finance/costs/${cost.id}`, { date_allocations: allocPayload(rows) });
      toast.success(rows.length ? "Cost split across dates — reports will use these dates" : "Date split removed");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title="Split Cost Across Supplier Invoice Dates" size="md" testid="alloc-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={save} disabled={saving} testid="alloc-save">{saving ? "Saving..." : "Save Split"}</Btn></>}>
      <div className="text-sm font-medium mb-1">{cost.description}</div>
      <div className="text-xs text-stone-500 mb-3">
        {cost.cost_date} · {fmtCurrency(cost.amount, cost.currency)} — split this cost into the actual supplier invoice
        dates and amounts. Monthly reports and the tariff model will use these dates instead of the capture date.
        Works on past and invoiced costs.
      </div>
      <AllocationsEditor rows={rows} setRows={setRows} amount={amount} currency={cost.currency} />
    </Modal>
  );
}


function CostModal({ initial, categories, suppliers, customers, onClose, onSavedNew }) {
  const isEdit = !!initial.id;
  const [split, setSplit] = useState(amountsOf(initial) || {});
  const [allocRows, setAllocRows] = useState((initial.date_allocations || []).map((a) => ({ date: a.date, amount: String(a.amount) })));
  const [form, setForm] = useState({
    ...emptyCost(),
    ...initial,
    unit: initial.unit || "Units",
    quantity: initial.quantity ?? 1,
    unit_price: initial.unit_price ?? "",
    category: initial.category || (categories[0]?.name || ""),
  });
  const [saving, setSaving] = useState(false);
  const amount = (Number(form.quantity) || 0) * (Number(form.unit_price) || 0);

  const save = async () => {
    if (!form.category) { toast.error("Select a category"); return; }
    if (!form.description?.trim()) { toast.error("Enter a description"); return; }
    if (!(Number(form.quantity) > 0)) { toast.error("Enter a quantity"); return; }
    if (!(Number(form.unit_price) > 0)) { toast.error("Enter a unit price"); return; }
    if (splitTotal(split) > amount + 0.01) { toast.error("Tagged amounts cannot exceed the cost amount"); return; }
    const allocTotal = allocRows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
    if (allocRows.length && Math.abs(allocTotal - amount) > 0.01) {
      toast.error("Allocated dates must add up to the full cost amount"); return;
    }
    setSaving(true);
    try {
      const p = {
        cost_date: form.cost_date,
        category: form.category,
        description: form.description,
        supplier_id: form.supplier_id || null,
        supplier_name: form.supplier_name || null,
        customer_id: form.customer_id || null,
        customer_name: form.customer_name || null,
        unit: form.unit || "Units",
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
        currency: "ZAR",
        notes: form.notes || null,
        ...classPayload(split, amount),
        recurring: !!form.recurring,
        date_allocations: allocPayload(allocRows),
      };
      if (isEdit) {
        await api.patch(`/finance/costs/${initial.id}`, p);
        toast.success("Cost updated");
        onClose();
      } else {
        const r = await api.post("/finance/costs", p);
        toast.success("Cost captured — now attach the supplier invoice + proof of payment");
        onSavedNew(r.data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? "Edit Cost" : "Capture Cost"} size="lg" testid="cost-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={save} disabled={saving} testid="cost-save">{saving ? "Saving..." : isEdit ? "Save Changes" : "Save & Attach Docs"}</Btn></>}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Cost Date" required>
          <Input type="date" value={form.cost_date} onChange={(e) => setForm({ ...form, cost_date: e.target.value })} data-testid="cost-date" />
        </Field>
        <Field label="Category" required>
          <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="cost-category">
            <option value="">—</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </Select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Description" required>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="e.g. Diesel for delivery truck — DN SD31248" data-testid="cost-description" />
          </Field>
        </div>
        <Field label="Unit" required>
          <Input list="cost-units" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                 placeholder="e.g. Tons" data-testid="cost-unit" />
          <datalist id="cost-units">{UNITS.map((u) => <option key={u} value={u} />)}</datalist>
        </Field>
        <Field label="Quantity" required>
          <Input type="number" step="0.001" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="cost-quantity" />
        </Field>
        <Field label="Unit Price (ZAR)" required>
          <Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="cost-unit-price" />
        </Field>
        <Field label="Amount (ZAR) — auto">
          <Input value={fmtCurrency(amount, "ZAR")} readOnly className="bg-stone-50" data-testid="cost-amount" />
        </Field>
        <Field label="Supplier (optional)">
          <Select value={form.supplier_id} onChange={(e) => {
            const s = suppliers.find((x) => x.id === e.target.value);
            setForm({ ...form, supplier_id: e.target.value, supplier_name: s?.name || "" });
          }} data-testid="cost-supplier">
            <option value="">—</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
        <Field label="Customer (optional)">
          <Select value={form.customer_id} onChange={(e) => {
            const c = customers.find((x) => x.id === e.target.value);
            setForm({ ...form, customer_id: e.target.value, customer_name: c?.name || "" });
          }} data-testid="cost-customer">
            <option value="">—</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Tariff Classification — tag actual amounts across the classes (Fixed / Consumables / Transport count as Variable, shown separately)">
            <ClassSplitEditor value={split} onChange={setSplit} amount={amount} />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Split across supplier invoice dates (optional) — reports use these dates instead of the cost date">
            <AllocationsEditor rows={allocRows} setRows={setAllocRows} amount={amount} currency="ZAR" />
          </Field>
        </div>
        <Field label="Recurring Monthly">
          <label className="flex items-center gap-2 px-3 py-2 border border-stone-300 rounded-sm text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} data-testid="cost-recurring" />
            <span className="text-stone-700">Auto-available every month for invoicing</span>
          </label>
        </Field>
        <div className="md:col-span-2">
          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

// ============ COST DOCS MODAL (supplier invoice + proof of payment) ============
const COST_DOC_CATEGORIES = ["supplier_invoice", "proof_of_payment", "other"];

function CostDocsModal({ cost, onClose }) {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("supplier_invoice");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const r = await api.get("/documents", { params: { linked_to: "cost", linked_id: cost.id } });
    setDocs(r.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        fd.append("linked_to", "cost");
        fd.append("linked_id", cost.id);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!confirm("Remove this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <Modal open onClose={onClose} title={`Attachments · ${cost.category} — ${cost.description}`} size="lg" testid="cost-docs-modal"
           footer={<Btn variant="secondary" onClick={onClose}>Done</Btn>}>
      <div className="flex items-end gap-2 mb-3 pb-3 border-b border-stone-200">
        <Field label="Document Type">
          <Select value={category} onChange={(e) => setCategory(e.target.value)} className="min-w-[200px]" data-testid="cost-doc-category">
            {COST_DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </Select>
        </Field>
        <input ref={fileRef} type="file" multiple onChange={onUpload} className="hidden" data-testid="cost-doc-file" />
        <Btn onClick={() => fileRef.current?.click()} disabled={uploading} testid="cost-doc-upload">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload"}
        </Btn>
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {docs.length === 0 && <div className="text-stone-400 text-sm py-6 text-center">No documents attached yet. Upload the supplier invoice and proof of payment.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-stone-50 border-b border-stone-100 text-sm">
            <FileText className="h-4 w-4 text-stone-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{d.original_filename}</div>
              <div className="text-xs text-stone-500">{d.category?.replace(/_/g, " ")} · {Math.round((d.size || 0) / 1024)} KB · {fmtDate(d.created_at)}</div>
            </div>
            <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-emerald-800 px-2 py-1" data-testid={`cost-doc-dl-${d.id}`}><Download className="h-4 w-4" /></a>
            <button onClick={() => del(d.id)} className="text-stone-500 hover:text-red-700 px-2 py-1" data-testid={`cost-doc-del-${d.id}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ============ CATEGORIES MODAL ============
function CategoriesModal({ categories, isAdmin, onClose }) {
  const [list, setList] = useState(categories);
  const [name, setName] = useState("");

  const reload = async () => { const r = await api.get("/finance/cost-categories"); setList(r.data); };

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/finance/cost-categories", { name: name.trim() }); setName(""); toast.success("Category added"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const del = async (id) => {
    if (!confirm("Delete this category?")) return;
    try { await api.delete(`/finance/cost-categories/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <Modal open onClose={onClose} title="Cost Categories" size="md" testid="categories-modal"
           footer={<Btn variant="secondary" onClick={onClose}>Done</Btn>}>
      <div className="flex items-end gap-2 mb-3">
        <Field label="New category">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maintenance" data-testid="new-category-name"
                 onKeyDown={(e) => e.key === "Enter" && add()} />
        </Field>
        <Btn onClick={add} testid="add-category-btn"><Plus className="h-4 w-4" /> Add</Btn>
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {list.map((c) => (
          <div key={c.id} className="flex items-center justify-between px-3 py-2 border-b border-stone-100 text-sm" data-testid={`category-row-${c.id}`}>
            <span>{c.name}</span>
            {isAdmin && (
              <button onClick={() => del(c.id)} className="text-stone-400 hover:text-red-700" title="Delete" data-testid={`category-del-${c.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ============ CREATE CUSTOMER INVOICE FROM COSTS ============
const remainingOf = (c) => Math.max(0, (Number(c.amount) || 0) - (Number(c.invoiced_amount) || 0));

function CreateInvoiceModal({ costs, customers, onClose, onCreated }) {
  const defaultCustomerId = costs.find((c) => c.customer_id)?.customer_id || "";
  const [form, setForm] = useState({
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    customer_id: defaultCustomerId,
    vat_rate: 15,
  });
  const [lines, setLines] = useState(costs.map((c) => ({
    cost_id: c.id,
    description: c.description || c.category,
    unit: c.unit || "",
    quantity: c.status === "partial" ? 1 : (Number(c.quantity) || 1),
    unit_price: c.status === "partial" ? remainingOf(c) : (Number(c.unit_price) || 0),
    vat_inclusive: false,
    close_cost: false,
  })));
  const [saving, setSaving] = useState(false);
  const [creditAvail, setCreditAvail] = useState(0);
  const [applyCredit, setApplyCredit] = useState("");

  // Auto-suggest the next invoice number for the selected customer (editable).
  useEffect(() => {
    if (!form.customer_id) return;
    api.get("/finance/next-invoice-number", { params: { customer_id: form.customer_id } })
      .then((r) => setForm((f) => ({ ...f, invoice_number: r.data.invoice_number })))
      .catch(() => {});
  }, [form.customer_id]); // eslint-disable-line

  // Load available customer credit so it can be applied at creation.
  useEffect(() => {
    setApplyCredit("");
    if (!form.customer_id) { setCreditAvail(0); return; }
    api.get(`/finance/customers/${form.customer_id}/credit`)
      .then((r) => setCreditAvail(r.data.available || 0))
      .catch(() => setCreditAvail(0));
  }, [form.customer_id]); // eslint-disable-line

  const setLine = (i, key, val) => setLines((ls) => ls.map((l, x) => (x === i ? { ...l, [key]: val } : l)));
  const costTotal = costs.reduce((a, c) => a + remainingOf(c), 0);
  const rate = Number(form.vat_rate) || 0;
  const f = 1 + rate / 100;
  const allIncl = lines.length > 0 && lines.every((l) => l.vat_inclusive);
  let subtotal = 0, vat = 0;
  for (const l of lines) {
    const amt = Number(l.quantity || 0) * Number(l.unit_price || 0);
    if (l.vat_inclusive && rate > 0) {
      const net = amt / f;
      subtotal += net;
      vat += amt - net;
    } else {
      subtotal += amt;
      vat += amt * rate / 100;
    }
  }
  const total = subtotal + vat;

  const create = async () => {
    if (!form.invoice_number.trim()) { toast.error("Enter an invoice number"); return; }
    const cust = customers.find((c) => c.id === form.customer_id);
    setSaving(true);
    try {
      await api.post("/finance/invoices/from-costs", {
        invoice_number: form.invoice_number.trim(),
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        customer_id: form.customer_id || null,
        customer_name: cust?.name || null,
        cost_ids: costs.map((c) => c.id),
        lines: lines.map((l) => ({
          cost_id: l.cost_id,
          description: l.description,
          unit: l.unit || null,
          quantity: Number(l.quantity || 0),
          unit_price: Number(l.unit_price || 0),
          vat_inclusive: !!l.vat_inclusive,
          close_cost: !!l.close_cost,
        })),
        vat_rate: Number(form.vat_rate),
        currency: "ZAR",
        ...(Number(applyCredit) > 0 ? { apply_credit: Number(applyCredit) } : {}),
      });
      toast.success("Customer invoice created from costs");
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Create Customer Invoice from Costs" size="xl" testid="create-invoice-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={create} disabled={saving} testid="create-invoice-save">{saving ? "Creating..." : "Create Invoice"}</Btn></>}>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
        <Field label="Invoice #" required>
          <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="cp-inv-number" />
        </Field>
        <Field label="Customer">
          <Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="cp-customer">
            <option value="">—</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Invoice Date" required>
          <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="cp-inv-date" />
        </Field>
        <Field label="Due Date">
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="cp-due-date" />
        </Field>
        <Field label="VAT %">
          <Input type="number" step="0.1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} />
        </Field>
      </div>

      <div className="text-xs text-stone-500 mb-2">
        Cost still to invoice: <span className="font-mono">{fmtCurrency(costTotal, "ZAR")}</span>.
        Edit the amounts below to invoice the customer your cost-plus price. Invoice less than a cost and the balance stays on the cost as <span className="font-semibold">partially invoiced</span> for a later invoice (or tick to close it). Tick <span className="font-semibold">Incl. VAT</span> on any line whose amount already includes VAT.
      </div>
      <div className="panel overflow-hidden mb-3">
        <table className="min-w-full text-sm">
          <thead className="bg-stone-100 border-b">
            <tr>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider">Description</th>
              <th className="px-2 py-2 text-left text-xs uppercase tracking-wider w-28">Unit</th>
              <th className="px-2 py-2 text-right text-xs uppercase tracking-wider w-24">Qty</th>
              <th className="px-2 py-2 text-right text-xs uppercase tracking-wider w-32">Unit Price</th>
              <th className="px-2 py-2 text-center text-xs uppercase tracking-wider w-24">
                <label className="flex items-center justify-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={allIncl}
                    onChange={(e) => setLines((ls) => ls.map((l) => ({ ...l, vat_inclusive: e.target.checked })))}
                    data-testid="cp-incl-vat-all" />
                  Incl. VAT
                </label>
              </th>
              <th className="px-2 py-2 text-right text-xs uppercase tracking-wider w-32">Line Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-b" data-testid={`cp-line-${i}`}>
                <td className="px-2 py-1.5">
                  <Input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`cp-line-desc-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <Input list="cp-units" value={l.unit || ""} onChange={(e) => setLine(i, "unit", e.target.value)} data-testid={`cp-line-unit-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <Input type="number" step="0.001" value={l.quantity} className="text-right" onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`cp-line-qty-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <Input type="number" step="0.01" value={l.unit_price} className="text-right" onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`cp-line-price-${i}`} />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input type="checkbox" checked={!!l.vat_inclusive} onChange={(e) => setLine(i, "vat_inclusive", e.target.checked)} data-testid={`cp-line-incl-${i}`} />
                </td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {(Number(l.quantity || 0) * Number(l.unit_price || 0)).toFixed(2)}
                  {l.vat_inclusive && rate > 0 && <span className="block text-[10px] text-stone-400">incl. VAT</span>}
                  {(() => {
                    const c = costs.find((x) => x.id === l.cost_id);
                    if (!c) return null;
                    const amt = Number(l.quantity || 0) * Number(l.unit_price || 0);
                    const left = remainingOf(c) - amt;
                    if (left <= 0.005) return null;
                    return (
                      <label className="block text-[10px] text-amber-700 font-sans cursor-pointer whitespace-nowrap" data-testid={`cp-line-remaining-${i}`}>
                        <input type="checkbox" className="mr-1 align-middle" checked={!!l.close_cost}
                          onChange={(e) => setLine(i, "close_cost", e.target.checked)} data-testid={`cp-line-close-${i}`} />
                        {l.close_cost ? "closing cost — no balance" : `${fmtCurrency(left, "ZAR")} left to invoice later`}
                      </label>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <datalist id="cp-units">{UNITS.map((u) => <option key={u} value={u} />)}</datalist>
      </div>

      <div className="border-t border-stone-200 pt-3 space-y-1 max-w-xs ml-auto text-sm">
        <div className="flex justify-between font-mono"><span className="text-stone-600">Subtotal (excl. VAT)</span><span>{fmtCurrency(subtotal, "ZAR")}</span></div>
        <div className="flex justify-between font-mono"><span className="text-stone-600">VAT ({form.vat_rate}%)</span><span>{fmtCurrency(vat, "ZAR")}</span></div>
        <div className="flex justify-between font-mono font-semibold text-emerald-900 border-t pt-1"><span>Total</span><span>{fmtCurrency(total, "ZAR")}</span></div>
        {form.customer_id && creditAvail > 0 && (
          <>
            <div className="flex justify-between items-center gap-2 text-violet-700 pt-1" data-testid="cp-credit-row">
              <span className="text-xs">Apply Credit <span className="text-stone-500">(avail {fmtCurrency(creditAvail, "ZAR")})</span></span>
              <Input type="number" step="0.01" className="w-28 text-right h-8" value={applyCredit}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(Number(e.target.value || 0), creditAvail, total));
                  setApplyCredit(e.target.value === "" ? "" : v);
                }}
                placeholder="0.00" data-testid="cp-apply-credit" />
            </div>
            <button type="button" className="text-xs text-violet-700 hover:underline w-full text-right"
              onClick={() => setApplyCredit(Math.min(creditAvail, total))} data-testid="cp-apply-max-credit">
              Apply max ({fmtCurrency(Math.min(creditAvail, total), "ZAR")})
            </button>
            <div className="flex justify-between font-mono font-semibold border-t pt-1" data-testid="cp-balance-due">
              <span>Balance Due</span><span>{fmtCurrency(Math.max(0, total - Number(applyCredit || 0)), "ZAR")}</span>
            </div>
          </>
        )}
      </div>
      <div className="text-xs text-stone-500 mt-3">
        All attachments from the {costs.length} selected cost(s) will be bundled onto this invoice.
      </div>
    </Modal>
  );
}
